import React from 'react';
import { MessageCircleIcon, EyeIcon, FileTextIcon, BuildingIcon, UserIcon, MapPinIcon, ClockIcon, TruckIcon } from 'lucide-react';
import { JobMessageButton } from '../../messaging/JobMessageButton';
interface Job {
  id: string;
  jobName: string;
  jobNumber: string;
  location: string;
  foreman: string;
  foremanPhone?: string;
  contractor: string;
  contractorId: string;
  assignedTrucks: string;
  status: 'in-progress' | 'completed' | 'pending';
  tonnageProgress: React.ReactNode;
  startDate: string;
  estimatedHours?: number;
  truckType?: string;
  material?: string;
}
interface BrokerInProgressJobsProps {
  jobs: Job[];
  onViewDetails?: (jobId: string) => void;
  onMessageForeman?: (jobId: string) => void;
  onMessageContractor?: (jobId: string, contractorId: string) => void;
  onUpdateStatus?: (jobId: string) => void;
}
export const BrokerInProgressJobs = ({
  jobs,
  onViewDetails,
  onMessageForeman,
  onMessageContractor,
  onUpdateStatus
}: BrokerInProgressJobsProps) => {
  // Filter only in-progress jobs
  const inProgressJobs = jobs.filter(job => job.status === 'in-progress');
  if (inProgressJobs.length === 0) {
    return <div className="bg-white rounded-md shadow overflow-hidden mt-4" data-id="element-716">
        <div className="flex items-center justify-between p-4 bg-gray-50" data-id="element-717">
          <div className="flex items-center gap-2" data-id="element-718">
            <span className="h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center text-white" data-id="element-719">
              <TruckIcon size={12} data-id="element-720" />
            </span>
            <h3 className="font-medium" data-id="element-721">Contractor-Assigned Jobs</h3>
            <span className="text-gray-500 text-sm" data-id="element-722">(0 active jobs)</span>
          </div>
        </div>
        <div className="p-8 text-center" data-id="element-723">
          <FileTextIcon size={40} className="mx-auto mb-3 text-gray-400" data-id="element-724" />
          <p className="text-gray-600 font-medium" data-id="element-725">
            No active jobs from contractors
          </p>
          <p className="text-sm text-gray-500 mt-1" data-id="element-726">
            When contractors assign jobs to you and they're in progress, they'll
            appear here
          </p>
        </div>
      </div>;
  }
  return <div className="bg-white rounded-md shadow overflow-hidden mt-4" data-id="element-727">
      <div className="flex items-center justify-between p-4 bg-gray-50" data-id="element-728">
        <div className="flex items-center gap-2" data-id="element-729">
          <span className="h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center text-white" data-id="element-730">
            <TruckIcon size={12} data-id="element-731" />
          </span>
          <h3 className="font-medium" data-id="element-732">Contractor-Assigned Jobs</h3>
          <span className="text-gray-500 text-sm" data-id="element-733">
            ({inProgressJobs.length} active jobs)
          </span>
        </div>
      </div>
      <div className="p-4" data-id="element-734">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-735">
          <thead className="bg-gray-50" data-id="element-736">
            <tr data-id="element-737">
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-738">
                Job Name
              </th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-739">
                Job #
              </th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-740">
                Location
              </th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-741">
                Foreman
              </th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-742">
                Contractor
              </th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-743">
                Trucks
              </th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-744">
                Progress
              </th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-745">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-746">
            {inProgressJobs.map(job => <tr key={job.id} className="hover:bg-gray-50" data-id="element-747">
                <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-748">
                  {job.jobName}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-749">
                  {job.jobNumber}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-750">
                  {job.location}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-751">
                  {job.foreman}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-752">
                  <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full" data-id="element-753">
                    {job.contractor}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-754">
                  {job.assignedTrucks}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-755">
                  {job.tonnageProgress}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-756">
                  <div className="flex space-x-2" data-id="element-757">
                    <button className="text-blue-600 hover:text-blue-800" onClick={() => onViewDetails?.(job.id)} data-id="element-758">
                      View
                    </button>
                    <JobMessageButton jobId={job.id} jobName={job.jobName} recipientRole="foreman" buttonText="Foreman" className="text-purple-600 hover:text-purple-800" data-id="element-759" />
                    <JobMessageButton jobId={job.id} jobName={job.jobName} recipientRole="contractor" buttonText="Contractor" className="text-green-600 hover:text-green-800" data-id="element-760" />
                    <JobMessageButton jobId={job.id} jobName={job.jobName} recipientRole="driver" buttonText="Drivers" className="text-indigo-600 hover:text-indigo-800" data-id="element-761" />
                    <JobMessageButton jobId={job.id} jobName={job.jobName} recipientRole="trucking" buttonText="Trucking" className="text-orange-600 hover:text-orange-800" data-id="element-762" />
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
};